import { cn } from '@ambernord/ui';
import { PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { type NavItem, visibleGroups } from './nav.js';

/**
 * Grupēta, ikonizēta kreisā navigācija.
 * Desktop (lg+): sakļaujama kolonna (ikonu režīms ar tooltip). Mobilais: pārklājuma atvilktne.
 * Skaitītāju nozīmītes (`badges`, ceļš → skaits) nāk no /api/dashboard/summary caur AppLayout.
 */

export interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  /** Ceļš (`to`) → skaitītājs; rāda tikai > 0. */
  badges: Record<string, number>;
}

function NavEntry({
  item,
  label,
  collapsed,
  count,
  onNavigate,
}: {
  item: NavItem;
  label: string;
  collapsed: boolean;
  count: number | undefined;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  const showCount = typeof count === 'number' && count > 0;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      title={collapsed ? label : undefined}
      onClick={onNavigate}
      className={({ isActive }: { isActive: boolean }) =>
        cn(
          'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition-colors duration-150',
          isActive
            ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
            : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-white',
          collapsed && 'justify-center px-2',
        )
      }
    >
      <span className="relative shrink-0">
        <Icon className="h-[18px] w-[18px]" aria-hidden />
        {collapsed && showCount ? (
          <span
            className="absolute -right-1.5 -top-1.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white dark:ring-slate-950"
            aria-hidden
          />
        ) : null}
      </span>
      {!collapsed ? <span className="min-w-0 flex-1 truncate">{label}</span> : null}
      {!collapsed && showCount ? (
        <span className="ml-auto inline-flex min-w-[1.375rem] items-center justify-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold leading-none text-amber-800 ring-1 ring-inset ring-amber-600/30 dark:bg-amber-900/50 dark:text-amber-300">
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </NavLink>
  );
}

function NavList({
  collapsed,
  badges,
  onNavigate,
}: {
  collapsed: boolean;
  badges: Record<string, number>;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const groups = visibleGroups(user?.role ?? '');

  return (
    <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3">
      {groups.map((group, gi) => (
        <Fragment key={group.key}>
          {collapsed ? (
            gi > 0 ? (
              <div
                className="mx-2 my-2.5 border-t border-slate-200 dark:border-slate-800"
                aria-hidden
              />
            ) : null
          ) : (
            <p
              className={cn(
                'px-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500',
                gi === 0 ? 'pt-1' : 'pt-4',
              )}
            >
              {t(`navGroups.${group.key}`)}
            </p>
          )}
          <div className="space-y-0.5">
            {group.items.map((item) => (
              <NavEntry
                key={item.to}
                item={item}
                label={t(`nav.${item.key}`)}
                collapsed={collapsed}
                count={badges[item.to]}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </Fragment>
      ))}
    </nav>
  );
}

export function Sidebar({
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onCloseMobile,
  badges,
}: SidebarProps) {
  const { t } = useTranslation();

  return (
    <>
      {/* Desktop: sakļaujamā kolonna */}
      <aside
        className={cn(
          'sticky top-0 z-30 hidden h-screen shrink-0 flex-col border-r border-slate-200 bg-white transition-[width] duration-200 ease-in-out dark:border-slate-800 dark:bg-slate-950 lg:flex',
          collapsed ? 'w-[4.25rem]' : 'w-60',
        )}
      >
        <NavList collapsed={collapsed} badges={badges} />
        <div className="border-t border-slate-200 p-2 dark:border-slate-800">
          <button
            type="button"
            onClick={onToggleCollapsed}
            title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-white',
              collapsed && 'justify-center px-2',
            )}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-[18px] w-[18px] shrink-0" aria-hidden />
            ) : (
              <>
                <PanelLeftClose className="h-[18px] w-[18px] shrink-0" aria-hidden />
                <span className="truncate">{t('sidebar.collapse')}</span>
              </>
            )}
          </button>
        </div>
      </aside>

      {/* Mobilais: pārklājuma atvilktne (transform + opacity pārejas) */}
      <div
        className={cn('fixed inset-0 z-[70] lg:hidden', mobileOpen ? '' : 'pointer-events-none')}
        aria-hidden={!mobileOpen}
      >
        <button
          type="button"
          tabIndex={mobileOpen ? 0 : -1}
          aria-label={t('sidebar.closeMenu')}
          onClick={onCloseMobile}
          className={cn(
            'absolute inset-0 h-full w-full cursor-default bg-slate-950/50 backdrop-blur-sm transition-opacity duration-200',
            mobileOpen ? 'opacity-100' : 'opacity-0',
          )}
        />
        <div
          className={cn(
            'absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col border-r border-slate-200 bg-white shadow-2xl transition-transform duration-200 ease-in-out dark:border-slate-800 dark:bg-slate-950',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800">
            <span className="text-base font-extrabold tracking-tight text-brand-700 dark:text-brand-400">
              AmberNord
            </span>
            <button
              type="button"
              onClick={onCloseMobile}
              aria-label={t('sidebar.closeMenu')}
              className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>
          <NavList collapsed={false} badges={badges} onNavigate={onCloseMobile} />
        </div>
      </div>
    </>
  );
}
